import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { User, CreditCard, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import BillingHistory from './BillingHistory';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccountSettings = ({ open, onOpenChange, user, trialUser, defaultTab = 'account', forceOpen = false, onLogout }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Update activeTab when defaultTab prop changes
  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);
  
  // Form states
  const [loginDetails, setLoginDetails] = useState({
    fullName: user?.full_name || trialUser?.name || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleUpgrade = async (planId) => {
    if (processingPayment) return;
    
    setProcessingPayment(true);
    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/checkout/create-session`, {
        plan_id: planId,
        origin_url: originUrl
      });
      
      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to start checkout process';
      toast.error(errorMessage);
      setProcessingPayment(false);
    }
  };

  const handleLoginUpdate = (e) => {
    e.preventDefault();
    // TODO: Implement login details update
    console.log('Update login details:', loginDetails);
  };

  return (
    <Dialog open={open} onOpenChange={forceOpen ? undefined : onOpenChange}>
      <DialogContent className="account-settings-modal max-w-3xl max-h-[90vh]" onPointerDownOutside={forceOpen ? (e) => e.preventDefault() : undefined}>
        <DialogHeader className="sticky top-0 bg-[rgba(15,15,20,0.95)] z-10 pb-4">
          <DialogTitle className="text-2xl font-bold" style={{ color: '#ffffff', fontSize: '1.75rem', fontWeight: '700' }}>
            Account Settings
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-100px)] pr-2">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-[rgba(30,30,35,0.8)]">
            <TabsTrigger value="login" className="data-[state=active]:bg-[rgba(212,175,55,0.2)] data-[state=active]:text-[#D4AF37]">
              <User className="w-4 h-4 mr-2" />
              Login Details
            </TabsTrigger>
            <TabsTrigger value="billing" className="data-[state=active]:bg-[rgba(212,175,55,0.2)] data-[state=active]:text-[#D4AF37]">
              <CreditCard className="w-4 h-4 mr-2" />
              Billing
            </TabsTrigger>
            <TabsTrigger value="plan" className="data-[state=active]:bg-[rgba(212,175,55,0.2)] data-[state=active]:text-[#D4AF37]">
              <Package className="w-4 h-4 mr-2" />
              Plan
            </TabsTrigger>
          </TabsList>

          {/* Login Details Tab */}
          <TabsContent value="login" className="mt-6">
            <Card className="bg-[rgba(20,20,25,0.8)] border-[rgba(212,175,55,0.3)]">
              <CardHeader>
                <CardTitle className="text-white" style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ffffff' }}>Login Information</CardTitle>
                <CardDescription style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Update your account credentials and personal information
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLoginUpdate} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="text-white">Full Name</Label>
                    <Input
                      id="fullName"
                      value={loginDetails.fullName}
                      onChange={(e) => setLoginDetails({...loginDetails, fullName: e.target.value})}
                      className="bg-[rgba(30,30,35,0.8)] border-[rgba(212,175,55,0.3)] text-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={loginDetails.email}
                      onChange={(e) => setLoginDetails({...loginDetails, email: e.target.value})}
                      className="bg-[rgba(30,30,35,0.8)] border-[rgba(212,175,55,0.3)] text-white"
                      disabled={!!trialUser}
                    />
                    {trialUser && (
                      <p className="text-sm text-gray-400">Sign up to change your email address</p>
                    )}
                  </div>

                  <div className="border-t border-[rgba(212,175,55,0.2)] pt-4 mt-6">
                    <h4 className="text-white font-semibold mb-4" style={{ fontSize: '1.125rem', fontWeight: '600', color: '#ffffff' }}>Change Password</h4>
                    
                    {!trialUser && (
                      <div className="space-y-2 mb-4">
                        <Label htmlFor="currentPassword" className="text-white">Current Password</Label>
                        <Input
                          id="currentPassword"
                          type="password"
                          value={loginDetails.currentPassword}
                          onChange={(e) => setLoginDetails({...loginDetails, currentPassword: e.target.value})}
                          className="bg-[rgba(30,30,35,0.8)] border-[rgba(212,175,55,0.3)] text-white"
                        />
                      </div>
                    )}

                    <div className="space-y-2 mb-4">
                      <Label htmlFor="newPassword" className="text-white">New Password</Label>
                      <Input
                        id="newPassword"
                        type="password"
                        value={loginDetails.newPassword}
                        onChange={(e) => setLoginDetails({...loginDetails, newPassword: e.target.value})}
                        className="bg-[rgba(30,30,35,0.8)] border-[rgba(212,175,55,0.3)] text-white"
                        disabled={!!trialUser}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-white">Confirm New Password</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={loginDetails.confirmPassword}
                        onChange={(e) => setLoginDetails({...loginDetails, confirmPassword: e.target.value})}
                        className="bg-[rgba(30,30,35,0.8)] border-[rgba(212,175,55,0.3)] text-white"
                        disabled={!!trialUser}
                      />
                    </div>

                    {trialUser && (
                      <p className="text-sm text-gray-400 mt-2">Sign up to set a password</p>
                    )}
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-[#D4AF37] to-[#E5B200] text-black font-bold hover:from-[#FFCE45] hover:to-[#E5B200]"
                    disabled={!!trialUser}
                  >
                    {trialUser ? 'Sign Up to Update' : 'Save Changes'}
                  </Button>
                  
                  {(user || trialUser) && onLogout && (
                    <div className="mt-4 pt-4 border-t border-[rgba(212,175,55,0.2)]">
                      <Button 
                        type="button"
                        onClick={() => {
                          onLogout();
                          onOpenChange(false);
                        }}
                        variant="outline"
                        className="w-full border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
                      >
                        Log Out
                      </Button>
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing" className="mt-6">
            <Card className="bg-[rgba(20,20,25,0.8)] border-[rgba(212,175,55,0.3)]">
              <CardHeader>
                <CardTitle className="text-white" style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ffffff' }}>Billing & Payments</CardTitle>
                <CardDescription style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  View your subscription and payment history
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(!user?.subscription_plan || user?.subscription_plan === 'trial') ? (
                  <div className="text-center py-8">
                    <CreditCard className="w-16 h-16 mx-auto mb-4 text-[#D4AF37]" />
                    <h3 className="text-xl font-semibold text-white mb-2" style={{ fontSize: '1.25rem', fontWeight: '600', color: '#ffffff' }}>No Active Subscription</h3>
                    <p className="mb-6" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      You&apos;re currently using the free trial. Upgrade to a paid plan to unlock more features and credits.
                    </p>
                    <Button 
                      className="bg-gradient-to-r from-[#D4AF37] to-[#E5B200] text-black font-bold hover:from-[#FFCE45] hover:to-[#E5B200]"
                      onClick={() => setActiveTab('plan')}
                    >
                      View Plans
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Current Subscription */}
                    <div className="bg-gradient-to-r from-[rgba(212,175,55,0.1)] to-[rgba(229,178,0,0.1)] p-6 rounded-lg border border-[#D4AF37]">
                      <h4 className="text-white font-semibold mb-4" style={{ fontSize: '1.25rem', fontWeight: '600', color: '#ffffff' }}>Current Subscription</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Plan</p>
                          <p className="text-lg font-semibold text-white capitalize">
                            {user?.subscription_plan?.replace('_', ' ')} Plan
                          </p>
                        </div>
                        <div>
                          <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Status</p>
                          <p className="text-lg font-semibold text-[#4CAF50]">Active</p>
                        </div>
                        {user?.subscription_start_date && (
                          <>
                            <div>
                              <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Started</p>
                              <p className="text-lg text-white">
                                {new Date(user.subscription_start_date).toLocaleDateString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Next Billing</p>
                              <p className="text-lg text-white">
                                {new Date(new Date(user.subscription_start_date).setMonth(new Date(user.subscription_start_date).getMonth() + 1)).toLocaleDateString()}
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="mt-4 pt-4 border-t border-[rgba(212,175,55,0.3)] flex gap-3">
                        <Button 
                          variant="outline" 
                          className="border-[#D4AF37] text-[#D4AF37] hover:bg-[rgba(212,175,55,0.1)]"
                          onClick={() => setActiveTab('plan')}
                        >
                          Change Plan
                        </Button>
                        <Button 
                          variant="outline" 
                          className="border-[#D4AF37] text-[#D4AF37] hover:bg-[rgba(212,175,55,0.1)]"
                          onClick={async () => {
                            try {
                              const response = await axios.post(`${API}/billing/customer-portal`, {
                                return_url: window.location.origin + '/dashboard'
                              });
                              if (response.data.url) {
                                window.location.href = response.data.url;
                              }
                            } catch (error) {
                              console.error('Error opening customer portal:', error);
                              toast.error('Unable to open billing portal. Please try again.');
                            }
                          }}
                        >
                          Manage Payment Method
                        </Button>
                      </div>
                      
                      {/* Cancel Subscription Option */}
                      <div className="mt-4 pt-4 border-t border-[rgba(212,175,55,0.2)]">
                        {user?.subscription_status === 'cancelling' ? (
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-[#FFA726] text-sm">Your subscription will end on {user?.subscription_cancel_at ? new Date(user.subscription_cancel_at).toLocaleDateString() : 'the end of the billing period'}</p>
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="border-[#4CAF50] text-[#4CAF50] hover:bg-[rgba(76,175,80,0.1)]"
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/subscription/reactivate`);
                                  toast.success('Subscription reactivated!');
                                  window.location.reload();
                                } catch (error) {
                                  toast.error('Failed to reactivate subscription');
                                }
                              }}
                            >
                              Keep Subscription
                            </Button>
                          </div>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-[rgba(255,255,255,0.5)] hover:text-[#EF5350] hover:bg-transparent"
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to cancel your subscription? You will continue to have access until the end of your billing period.')) {
                                try {
                                  const response = await axios.post(`${API}/subscription/cancel`);
                                  toast.success(response.data.message);
                                  window.location.reload();
                                } catch (error) {
                                  toast.error('Failed to cancel subscription');
                                }
                              }
                            }}
                          >
                            Cancel Subscription
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Payment History */}
                    <div className="bg-[rgba(30,30,35,0.8)] p-6 rounded-lg border border-[rgba(212,175,55,0.3)]">
                      <h4 className="text-white font-semibold mb-4" style={{ fontSize: '1.25rem', fontWeight: '600', color: '#ffffff' }}>Payment History</h4>
                      <BillingHistory userId={user?.id} />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Plan Tab */}
          <TabsContent value="plan" className="mt-6">
            <Card className="bg-[rgba(20,20,25,0.8)] border-[rgba(212,175,55,0.3)]">
              <CardHeader>
                <CardTitle className="text-white" style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ffffff' }}>Current Plan</CardTitle>
                <CardDescription style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Manage your subscription and explore available plans
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Show trial plan for users without subscription (no subscription_plan field or subscription_plan is null/trial) */}
                {(!user?.subscription_plan || user?.subscription_plan === 'trial') ? (
                  <div className="space-y-4">
                    {/* Current Plan - Trial */}
                    <div className="bg-gradient-to-r from-[rgba(212,175,55,0.2)] to-[rgba(229,178,0,0.2)] p-6 rounded-lg border border-[#D4AF37]">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-2xl font-bold text-white" style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ffffff' }}>Trial</h3>
                          <p className="mt-1" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Current Plan</p>
                        </div>
                        <div className="text-4xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#E5B200] bg-clip-text text-transparent">
                          FREE
                        </div>
                      </div>
                      <ul className="space-y-2 mb-4" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                        <li>✓ 20 credits for trialling</li>
                      </ul>
                      <p style={{ color: '#00D4FF', fontWeight: '700', fontSize: '0.85rem', marginBottom: '0.75rem', textAlign: 'center' }}>Upgrade to Basic Plan — 50% Off Early Access</p>
                      <Button 
                        className="w-full bg-gradient-to-r from-[#D4AF37] to-[#E5B200] text-black font-bold hover:from-[#FFCE45] hover:to-[#E5B200]"
                        onClick={() => handleUpgrade('basic')}
                        disabled={processingPayment}
                      >
                        {processingPayment ? 'Processing...' : 'Upgrade to Basic — £14.99/mo'}
                      </Button>
                    </div>

                    {/* Sale Banner */}
                    <div style={{ background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(0, 153, 255, 0.06))', border: '1px solid rgba(0, 212, 255, 0.3)', borderRadius: '10px', padding: '0.75rem 1.25rem', textAlign: 'center' }}>
                      <span style={{ color: '#00D4FF', fontWeight: '700', fontSize: '0.85rem' }}>EARLY ACCESS SALE — 50% OFF ALL PLANS</span>
                      <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', marginTop: '0.25rem' }}>Lock in your rate before prices return to normal</p>
                    </div>

                    {/* Available Plans */}
                    <div className="mt-6">
                      <h4 className="text-white font-semibold mb-4" style={{ fontSize: '1.125rem', fontWeight: '600', color: '#ffffff' }}>Available Plans</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Basic Plan */}
                        <div className="bg-[rgba(30,30,35,0.8)] p-6 rounded-lg border border-[rgba(212,175,55,0.3)]">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <h3 className="text-xl font-bold text-white" style={{ fontSize: '1.25rem', fontWeight: '700', color: '#ffffff' }}>Basic Plan</h3>
                            <span style={{ background: 'linear-gradient(135deg, #00D4FF, #0099FF)', color: '#fff', fontSize: '0.7rem', fontWeight: '800', padding: '0.2rem 0.6rem', borderRadius: '50px' }}>50% OFF</span>
                          </div>
                          <div className="text-3xl font-bold text-[#D4AF37] mb-4">
                            <span style={{ fontSize: '1rem', color: 'rgba(0, 212, 255, 0.5)', textDecoration: 'line-through', marginRight: '0.5rem' }}>£29.99</span>
                            £14.99<span className="text-lg" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>/month</span>
                          </div>
                          <ul className="space-y-2 mb-4" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                            <li>✓ Access to all tools</li>
                            <li>✓ 250 monthly credits</li>
                            <li>✓ Request new features</li>
                            <li>✓ Premium support</li>
                          </ul>
                          <Button 
                            className="w-full bg-gradient-to-r from-[#D4AF37] to-[#E5B200] text-black hover:opacity-90"
                            onClick={() => handleUpgrade('basic')}
                            disabled={processingPayment}
                          >
                            {processingPayment ? 'Processing...' : 'Lock In This Price'}
                          </Button>
                        </div>

                        {/* Pro Plan */}
                        <div className="bg-[rgba(30,30,35,0.8)] p-6 rounded-lg border border-[rgba(212,175,55,0.3)]">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <h3 className="text-xl font-bold text-white" style={{ fontSize: '1.25rem', fontWeight: '700', color: '#ffffff' }}>Pro Plan</h3>
                            <span style={{ background: 'linear-gradient(135deg, #00D4FF, #0099FF)', color: '#fff', fontSize: '0.7rem', fontWeight: '800', padding: '0.2rem 0.6rem', borderRadius: '50px' }}>50% OFF</span>
                          </div>
                          <div className="text-3xl font-bold text-[#D4AF37] mb-4">
                            <span style={{ fontSize: '1rem', color: 'rgba(0, 212, 255, 0.5)', textDecoration: 'line-through', marginRight: '0.5rem' }}>£49.99</span>
                            £24.99<span className="text-lg" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>/month</span>
                          </div>
                          <ul className="space-y-2 mb-4" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                            <li>✓ Everything in Basic Plan</li>
                            <li>✓ 600 monthly credits</li>
                          </ul>
                          <Button 
                            className="w-full bg-gradient-to-r from-[#D4AF37] to-[#E5B200] text-black hover:opacity-90"
                            onClick={() => handleUpgrade('pro')}
                            disabled={processingPayment}
                          >
                            {processingPayment ? 'Processing...' : 'Lock In This Price'}
                          </Button>
                        </div>

                        {/* Small Law Firm Plan */}
                        <div className="bg-[rgba(30,30,35,0.8)] p-6 rounded-lg border border-[rgba(212,175,55,0.3)]">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <h3 className="text-xl font-bold text-white" style={{ fontSize: '1.25rem', fontWeight: '700', color: '#ffffff' }}>Small Law Firm Plan</h3>
                            <span style={{ background: 'linear-gradient(135deg, #00D4FF, #0099FF)', color: '#fff', fontSize: '0.7rem', fontWeight: '800', padding: '0.2rem 0.6rem', borderRadius: '50px' }}>50% OFF</span>
                          </div>
                          <div className="text-3xl font-bold text-[#D4AF37] mb-4">
                            <span style={{ fontSize: '1rem', color: 'rgba(0, 212, 255, 0.5)', textDecoration: 'line-through', marginRight: '0.5rem' }}>£199.99</span>
                            £99.99<span className="text-lg" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>/month</span>
                          </div>
                          <ul className="space-y-2 mb-4" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                            <li>✓ Everything in Basic Plan</li>
                            <li>✓ 2000 monthly credits</li>
                          </ul>
                          <Button 
                            className="w-full bg-gradient-to-r from-[#D4AF37] to-[#E5B200] text-black hover:opacity-90"
                            onClick={() => handleUpgrade('small_law_firm')}
                            disabled={processingPayment}
                          >
                            {processingPayment ? 'Processing...' : 'Lock In This Price'}
                          </Button>
                        </div>

                        {/* Enterprise Plan */}
                        <div className="bg-gradient-to-r from-[rgba(212,175,55,0.2)] to-[rgba(229,178,0,0.2)] p-6 rounded-lg border border-[#D4AF37]">
                          <h3 className="text-xl font-bold text-white mb-2" style={{ fontSize: '1.25rem', fontWeight: '700', color: '#ffffff' }}>Enterprise Plan</h3>
                          <div className="text-3xl font-bold text-[#D4AF37] mb-4">
                            Custom Pricing
                          </div>
                          <ul className="space-y-2 mb-4" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                            <li>✓ For large law firms</li>
                            <li>✓ Unlimited credits</li>
                            <li>✓ Dedicated account manager</li>
                            <li>✓ Custom integrations</li>
                          </ul>
                          <Button 
                            className="w-full bg-gradient-to-r from-[#D4AF37] to-[#E5B200] text-black hover:opacity-90"
                            onClick={() => window.location.href = '/request-quote'}
                          >
                            Request Quote
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Current Plan Display */}
                    {(() => {
                      const planDetails = {
                        basic: { name: 'Basic Plan', price: '£29.99', credits: 250, order: 1 },
                        pro: { name: 'Pro Plan', price: '£49.99', credits: 600, order: 2 },
                        small_law_firm: { name: 'Small Law Firm Plan', price: '£199.99', credits: 2000, order: 3 }
                      };
                      const currentPlan = planDetails[user?.subscription_plan] || planDetails.basic;
                      const currentPlanId = user?.subscription_plan || 'basic';
                      const currentPlanOrder = currentPlan.order;
                      
                      return (
                        <>
                          {/* Current Plan Header */}
                          <div className="bg-gradient-to-r from-[rgba(212,175,55,0.2)] to-[rgba(229,178,0,0.2)] p-6 rounded-lg border border-[#D4AF37]">
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <h3 className="text-2xl font-bold text-white" style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ffffff' }}>{currentPlan.name}</h3>
                                <p className="mt-1" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Active subscription</p>
                              </div>
                              <div className="text-4xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#E5B200] bg-clip-text text-transparent">
                                {currentPlan.price}<span className="text-lg">/mo</span>
                              </div>
                            </div>
                            <ul className="space-y-2 mb-4" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                              <li>✓ Access to all tools</li>
                              <li>✓ {currentPlan.credits} monthly credits</li>
                              <li>✓ Request new features</li>
                              <li>✓ Premium support</li>
                            </ul>
                            <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              Next billing date: {user?.subscription_start_date 
                                ? new Date(new Date(user.subscription_start_date).setMonth(new Date(user.subscription_start_date).getMonth() + 1)).toLocaleDateString()
                                : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                            </p>
                          </div>

                          {/* All Available Plans */}
                          <div className="mt-6">
                            <h4 className="text-white font-semibold mb-4" style={{ fontSize: '1.125rem', fontWeight: '600', color: '#ffffff' }}>Change Plan</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              
                              {/* Basic Plan */}
                              <div className={`p-6 rounded-lg border ${currentPlanId === 'basic' ? 'bg-gradient-to-r from-[rgba(212,175,55,0.2)] to-[rgba(229,178,0,0.2)] border-[#D4AF37]' : 'bg-[rgba(30,30,35,0.8)] border-[rgba(212,175,55,0.3)]'}`}>
                                {currentPlanId === 'basic' && (
                                  <span className="inline-block px-2 py-1 text-xs font-semibold bg-[#D4AF37] text-black rounded mb-3">CURRENT PLAN</span>
                                )}
                                <h3 className="text-xl font-bold text-white mb-2" style={{ fontSize: '1.25rem', fontWeight: '700', color: '#ffffff' }}>Basic Plan</h3>
                                <div className="text-3xl font-bold text-[#D4AF37] mb-4">
                                  £29.99<span className="text-lg" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>/month</span>
                                </div>
                                <ul className="space-y-2 mb-4" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                  <li>✓ Access to all tools</li>
                                  <li>✓ 250 monthly credits</li>
                                  <li>✓ Premium support</li>
                                </ul>
                                {currentPlanId !== 'basic' && (
                                  <Button 
                                    variant="outline"
                                    className="w-full border-[rgba(212,175,55,0.5)] text-[#D4AF37] hover:bg-[rgba(212,175,55,0.1)]"
                                    onClick={() => handleUpgrade('basic')}
                                    disabled={processingPayment}
                                  >
                                    {processingPayment ? 'Processing...' : 'Downgrade to Basic'}
                                  </Button>
                                )}
                              </div>

                              {/* Pro Plan */}
                              <div className={`p-6 rounded-lg border ${currentPlanId === 'pro' ? 'bg-gradient-to-r from-[rgba(212,175,55,0.2)] to-[rgba(229,178,0,0.2)] border-[#D4AF37]' : 'bg-[rgba(30,30,35,0.8)] border-[rgba(212,175,55,0.3)]'}`}>
                                {currentPlanId === 'pro' && (
                                  <span className="inline-block px-2 py-1 text-xs font-semibold bg-[#D4AF37] text-black rounded mb-3">CURRENT PLAN</span>
                                )}
                                <h3 className="text-xl font-bold text-white mb-2" style={{ fontSize: '1.25rem', fontWeight: '700', color: '#ffffff' }}>Pro Plan</h3>
                                <div className="text-3xl font-bold text-[#D4AF37] mb-4">
                                  £49.99<span className="text-lg" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>/month</span>
                                </div>
                                <ul className="space-y-2 mb-4" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                  <li>✓ Everything in Basic</li>
                                  <li>✓ 600 monthly credits</li>
                                </ul>
                                {currentPlanId !== 'pro' && (
                                  <Button 
                                    className={currentPlanOrder < 2 
                                      ? "w-full bg-gradient-to-r from-[#D4AF37] to-[#E5B200] text-black hover:opacity-90"
                                      : "w-full border-[rgba(212,175,55,0.5)] text-[#D4AF37] hover:bg-[rgba(212,175,55,0.1)]"
                                    }
                                    variant={currentPlanOrder < 2 ? "default" : "outline"}
                                    onClick={() => handleUpgrade('pro')}
                                    disabled={processingPayment}
                                  >
                                    {processingPayment ? 'Processing...' : (currentPlanOrder < 2 ? 'Upgrade to Pro' : 'Downgrade to Pro')}
                                  </Button>
                                )}
                              </div>

                              {/* Small Law Firm Plan */}
                              <div className={`p-6 rounded-lg border ${currentPlanId === 'small_law_firm' ? 'bg-gradient-to-r from-[rgba(212,175,55,0.2)] to-[rgba(229,178,0,0.2)] border-[#D4AF37]' : 'bg-[rgba(30,30,35,0.8)] border-[rgba(212,175,55,0.3)]'}`}>
                                {currentPlanId === 'small_law_firm' && (
                                  <span className="inline-block px-2 py-1 text-xs font-semibold bg-[#D4AF37] text-black rounded mb-3">CURRENT PLAN</span>
                                )}
                                <h3 className="text-xl font-bold text-white mb-2" style={{ fontSize: '1.25rem', fontWeight: '700', color: '#ffffff' }}>Small Law Firm</h3>
                                <div className="text-3xl font-bold text-[#D4AF37] mb-4">
                                  £199.99<span className="text-lg" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>/month</span>
                                </div>
                                <ul className="space-y-2 mb-4" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                  <li>✓ Everything in Basic</li>
                                  <li>✓ 2000 monthly credits</li>
                                </ul>
                                {currentPlanId !== 'small_law_firm' && (
                                  <Button 
                                    className="w-full bg-gradient-to-r from-[#D4AF37] to-[#E5B200] text-black hover:opacity-90"
                                    onClick={() => handleUpgrade('small_law_firm')}
                                    disabled={processingPayment}
                                  >
                                    {processingPayment ? 'Processing...' : 'Upgrade to Small Law Firm'}
                                  </Button>
                                )}
                              </div>
                            </div>

                            {/* Enterprise Plan - Always show */}
                            <div className="mt-4 bg-gradient-to-r from-[rgba(30,30,35,0.8)] to-[rgba(40,40,45,0.8)] p-6 rounded-lg border border-[rgba(212,175,55,0.3)]">
                              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                <div>
                                  <h3 className="text-xl font-bold text-white mb-1" style={{ fontSize: '1.25rem', fontWeight: '700', color: '#ffffff' }}>Enterprise Plan</h3>
                                  <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>For large law firms • Unlimited credits • Dedicated account manager • Custom integrations</p>
                                </div>
                                <Button 
                                  variant="outline"
                                  className="border-[#D4AF37] text-[#D4AF37] hover:bg-[rgba(212,175,55,0.1)] whitespace-nowrap"
                                  onClick={() => window.location.href = '/request-quote'}
                                >
                                  Request Quote
                                </Button>
                              </div>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccountSettings;
